import { PaginationInfo, Time } from "@core/shared/types";
import { atom } from "@mongez/react-atom";

export type NotificationType =
  | "invoiceApproved"
  | "newLesson"
  | "newShort"
  | "newOffer"
  | "newQuiz"
  | "newAnnouncement"
  | "quizResultReady"
  | "liveSessionReminder"
  | "subscriptionExpiryReminder"
  | "trainingGroundReviewDue"
  | "trainingGroundQuestionFeedbackResolved"
  | "gamificationLevelUp"
  | "gamificationLeagueResult"
  | "leagueMonthResult"
  | "leagueYearResult"
  | "leaguePassed"
  | "leagueLastDay"
  | "goldStorePresentDelivered"
  | "goldStorePresentCancelled"
  | "certificateIssued"
  | "quizNeedsReview";

export type NotificationChannel = "inApp" | "telegram" | "push";

export type Notification = {
  id: number;
  title: string;
  message: string;
  type: NotificationType | string;
  icon?: string;
  isRead: boolean;
  readAt?: Time | null;
  metadata?: Record<string, any> | null;
  createdAt: Time | number;
};

export type NotificationsResponse = {
  notifications: Notification[];
  paginationInfo: PaginationInfo | null;
};

export type NotificationsState = {
  notifications: Notification[];
  unreadCount: number;
  paginationInfo: PaginationInfo | null;
};

export const notificationsAtom = atom<NotificationsState>({
  key: "notifications",
  default: {
    notifications: [],
    unreadCount: 0,
    paginationInfo: null,
  },
});

/**
 * Prepend a freshly-received notification and bump the unread count.
 */
export function prependNotification(notification: Notification) {
  const current = notificationsAtom.value;

  // Avoid duplicates if the same notification arrives twice.
  if (current.notifications.some((item) => item.id === notification.id)) {
    return;
  }

  notificationsAtom.merge({
    notifications: [notification, ...current.notifications],
    unreadCount: notification.isRead
      ? current.unreadCount
      : current.unreadCount + 1,
  });
}

export function setUnreadCount(count: number) {
  notificationsAtom.change("unreadCount", count);
}
